import React, { useState } from 'react';
import { studentService } from '../../services/index.js';
import { Key, Eye, EyeOff, Copy, Check, RefreshCw, X, ShieldAlert, ShieldCheck } from 'lucide-react';

const generateSecurePassword = () => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const numbers = '23456789';
  const symbols = '!@#$%^&*';
  const all = upper + lower + numbers + symbols;

  let pwd = '';
  // Ensure at least one from each category
  pwd += upper.charAt(Math.floor(Math.random() * upper.length));
  pwd += lower.charAt(Math.floor(Math.random() * lower.length));
  pwd += numbers.charAt(Math.floor(Math.random() * numbers.length));
  pwd += symbols.charAt(Math.floor(Math.random() * symbols.length));

  for (let i = 0; i < 6; i++) {
    pwd += all.charAt(Math.floor(Math.random() * all.length));
  }

  // Shuffle
  return pwd.split('').sort(() => 0.5 - Math.random()).join('');
};

const ResetStudentPasswordModal = ({ isOpen, onClose, student, showNotification }) => {
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen || !student) return null;

  const handleGenerate = () => {
    const generated = generateSecurePassword();
    setNewPassword(generated);
    setTempPassword(generated);
    setError('');
  };

  const handleReset = async (e) => {
    e?.preventDefault();
    if (!newPassword || newPassword.trim().length < 4) {
      setError('Password must be at least 4 characters long');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await studentService.adminResetStudentPassword(student.studentId, newPassword.trim());
      setTempPassword(newPassword.trim());
      setResetSuccess(true);
      if (showNotification) {
        showNotification('success', 'Password Reset', `Password for ${student.name || student.studentId} has been reset.`);
      }
    } catch (err) {
      console.error('Reset password error:', err);
      const errMsg = err?.response?.data?.message || err?.message || 'Failed to reset password. Please verify permissions.';
      setError(errMsg);
      if (showNotification) {
        showNotification('error', 'Reset Failed', errMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCredentials = () => {
    const text = `Student Login\nStudent ID: ${student.studentId}\nTemporary Password: ${tempPassword}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const handleClose = () => {
    setNewPassword('');
    setTempPassword('');
    setShowPassword(false);
    setResetSuccess(false);
    setCopied(false);
    setError('');
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.6)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99999,
      padding: '16px'
    }}>
      <div style={{
        background: '#ffffff',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '520px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        overflow: 'hidden',
        border: '1px solid #e2e8f0'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#f8fafc'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Key size={20} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
                Reset Student Password
              </h3>
              <p style={{ margin: 0, fontSize: '12.5px', color: '#64748b' }}>
                Securely generate or assign a new password
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '8px'
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '24px' }}>
          {/* Student Info Card */}
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            padding: '14px 16px',
            marginBottom: '20px',
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '10px',
            fontSize: '13px'
          }}>
            <div>
              <span style={{ color: '#64748b', display: 'block', fontSize: '11px', textTransform: 'uppercase', fontWeight: '600' }}>Student Name</span>
              <strong style={{ color: '#1e293b' }}>{student.name || '—'}</strong>
            </div>
            <div>
              <span style={{ color: '#64748b', display: 'block', fontSize: '11px', textTransform: 'uppercase', fontWeight: '600' }}>Student ID</span>
              <strong style={{ color: '#1e293b' }}>{student.studentId}</strong>
            </div>
            <div>
              <span style={{ color: '#64748b', display: 'block', fontSize: '11px', textTransform: 'uppercase', fontWeight: '600' }}>Class & Section</span>
              <span style={{ color: '#334155' }}>
                {student.className || student.class || '—'} - {student.section || '—'}
              </span>
            </div>
            <div>
              <span style={{ color: '#64748b', display: 'block', fontSize: '11px', textTransform: 'uppercase', fontWeight: '600' }}>Parent Mobile</span>
              <span style={{ color: '#334155' }}>
                {student.parentPhone || student.fatherPhone || student.phone || '—'}
              </span>
            </div>
          </div>

          {/* Current Password Policy Note */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            background: '#f1f5f9',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '12.5px'
          }}>
            <span style={{ color: '#475569', fontWeight: '500' }}>Current Password:</span>
            <span style={{ fontFamily: 'monospace', letterSpacing: '2px', color: '#64748b', fontWeight: '600' }}>●●●●●●●●</span>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 8px',
              borderRadius: '9999px',
              fontSize: '11px',
              background: '#dcfce7',
              color: '#166534',
              fontWeight: '600'
            }}>
              <ShieldCheck size={12} />
              BCrypt Hash Encrypted
            </span>
          </div>

          {resetSuccess ? (
            /* Post-Reset Credentials Screen */
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              <div style={{
                width: '52px',
                height: '52px',
                borderRadius: '50%',
                background: '#dcfce7',
                color: '#16a34a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <Check size={28} />
              </div>
              <h4 style={{ margin: '0 0 6px 0', fontSize: '17px', color: '#166534', fontWeight: '700' }}>
                Password Reset Successfully
              </h4>
              <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#64748b' }}>
                The new password hash has been saved. Please share the temporary credentials with the parent/student.
              </p>

              <div style={{
                background: '#fef3c7',
                border: '1px solid #fde047',
                borderRadius: '10px',
                padding: '14px',
                marginBottom: '16px',
                textAlign: 'left'
              }}>
                <div style={{ fontSize: '11px', color: '#92400e', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Temporary Credential (One-time Display)
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#78350f' }}>Student ID: <strong>{student.studentId}</strong></div>
                    <div style={{ fontSize: '15px', color: '#1e293b', fontWeight: '700', fontFamily: 'monospace', marginTop: '2px' }}>
                      Password: {tempPassword}
                    </div>
                  </div>
                  <button
                    onClick={handleCopyCredentials}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      background: '#d97706',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <div style={{
                padding: '10px 14px',
                background: '#fee2e2',
                borderRadius: '8px',
                color: '#991b1b',
                fontSize: '12px',
                textAlign: 'left',
                display: 'flex',
                gap: '8px',
                marginBottom: '20px'
              }}>
                <ShieldAlert size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>
                  <strong>Security Reminder:</strong> For safety, this temporary password is only displayed once and will NOT be retrievable from the database once this dialog is closed.
                </span>
              </div>

              <button
                onClick={handleClose}
                style={{
                  width: '100%',
                  padding: '11px',
                  background: '#0f172a',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Done
              </button>
            </div>
          ) : (
            /* Reset Password Form */
            <form onSubmit={handleReset}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>
                    New Password <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <button
                    type="button"
                    onClick={handleGenerate}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      background: 'none',
                      border: 'none',
                      color: '#2563eb',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      padding: 0
                    }}
                  >
                    <RefreshCw size={13} />
                    Generate Strong Password
                  </button>
                </div>

                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setError('');
                    }}
                    placeholder="Enter at least 4 characters (e.g. 1234)"
                    style={{
                      width: '100%',
                      padding: '10px 42px 10px 14px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: '#64748b',
                      cursor: 'pointer',
                      padding: 0
                    }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '4px' }}>
                  Minimum 4 characters (e.g. 1234). Passwords are saved with one-way BCrypt encryption.
                </div>
              </div>

              {error && (
                <div style={{
                  padding: '10px 14px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  color: '#dc2626',
                  fontSize: '12.5px',
                  marginBottom: '16px'
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  style={{
                    padding: '10px 18px',
                    background: '#f1f5f9',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '13.5px',
                    fontWeight: '600',
                    color: '#475569',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !newPassword}
                  style={{
                    padding: '10px 20px',
                    background: loading || !newPassword ? '#94a3b8' : '#dc2626',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13.5px',
                    fontWeight: '600',
                    cursor: loading || !newPassword ? 'not-allowed' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetStudentPasswordModal;
